import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {SimpleEvent} from './simple-event.js';
import {stringify} from './functions/hash.js';
import {StateContext, useStateContext} from './state-context.js';

// consumer of the SizeContext and this sets the size throught the provided dispatcher
function SizeWatcher(props) {

    const element = props.element ? props.element : window;
    const [state, dispatch] = useStateContext([props.stateName]);
    const stateValue = state[props.stateName];

    useEffect(() => {

            // we do not continiously update, but only after 500 ms of no resize changes
            let timerId = null;
            const onResize = (event) => {
                clearTimeout(timerId);
                timerId = setTimeout(() => {
                    const width = element.clientWidth || (element.offsetWidth || 1200);
                    const height = element.clientHight || (element.offsetHeight || 0);
                    if (stringify(props.parseValue(width, height)) === stringify(stateValue)) { 
                        return;
                    }
                    dispatch({type: 'setSize', value: [width, height]});
                }, 500);
            }

            // set resize events
            // @todo checking only on window resize is not completely safe. The size of the elements can change
            // due to other dynamic behaviour. We could look into ResizeObserver.
            const event = new SimpleEvent(window, 'resize', onResize);

            onResize();

            // return function to cleanup the events listeners when component is unmounted
            return () => {
                event.remove();
                clearTimeout(timerId);
            }
        },
        [element, dispatch, stateValue, props]);

    return <>
        {props.children}
    </>;

}


const defaultGetSize = function (width) {
    let size = 'large';
    if (width < 600) {
        size = 'small';
    } else if (width < 1000) {
        size = 'medium';
    }
    return size;
}

function SizeContext(props) {


    const initialState = {};
    initialState[props.stateName] = props.getSize(1280);

    const reducer = (state, action) => {
        if (action.type === 'setSize') {
            // if something changes, always create a new object
            const nextSize = {};
            const size = props.getSize(action.value[0], action.value[1]);
            nextSize[props.stateName] = size;
            return {
                ...state,
                ...nextSize
            };
        }

        // by default return the same object: nothing changes
        return state;
    };

    return <StateContext reducer={reducer} initialState={initialState}>
        <SizeWatcher stateName={props.stateName} element={props.element} parseValue={props.getSize}>
            {props.children}
        </SizeWatcher>
    </StateContext>;
}

SizeContext.defaultProps = {
    stateName: 'renderSize',
    getSize: defaultGetSize,
    element: null
}

SizeContext.propTypes = {
    stateName: PropTypes.string,
    getSize: PropTypes.func,
    element: PropTypes.instanceOf(Element)
};

export {SizeContext};